<template>
    <div v-if="mentions.length" ref="mirror" class="composer-mention-highlights fs-mask" :style="mirrorStyle" aria-hidden="true">
        <template v-for="(segment, index) in segments" :key="index">
            <span
                v-if="segment.mention"
                class="composer-mention-pill"
                :class="segment.status && `composer-mention-pill-${segment.status}`"
                :data-status="segment.status ?? undefined"
                data-testid="composer-mention-tag"
                ><span class="composer-mention-at">{{ segment.text.slice(0, 1) }}</span
                ><span class="composer-mention-name">{{ segment.text.slice(1) }}</span></span
            >
            <template v-else>{{ segment.text }}</template>
        </template>
    </div>
</template>

<script setup lang="ts">
import { useEventListener, useResizeObserver } from "@vueuse/core";
import type { ComposerMention } from "~vue/api/generated/chat";
import type { Span } from "~vue/utils/pii/keywordClassifier";
import { computed, type CSSProperties, nextTick, ref, useTemplateRef, watch } from "vue";

// A textarea can only paint plain text, so the tags are drawn on a mirror of it underneath and the
// textarea's own text goes transparent while any are present. Wrapping must match to the pixel,
// which is why the mirror copies the textarea's measured box and type instead of sharing classes.
const {
    warnSpans = [],
    errorSpans = [],
    ...props
} = defineProps<{
    text: string;
    mentions: ComposerMention[];
    textarea: HTMLTextAreaElement | null | undefined;
    warnSpans?: Span[];
    errorSpans?: Span[];
}>();
const mirror = useTemplateRef<HTMLDivElement>("mirror");
const mirrorStyle = ref<CSSProperties>({});

const COPIED_PROPERTIES = [
    "fontFamily",
    "fontSize",
    "fontWeight",
    "fontStyle",
    "fontStretch",
    "fontKerning",
    "fontFeatureSettings",
    "fontVariationSettings",
    "lineHeight",
    "letterSpacing",
    "wordSpacing",
    "textIndent",
    "textTransform",
    "tabSize",
    "paddingTop",
    "paddingRight",
    "paddingBottom",
    "paddingLeft",
] as const;

type PillStatus = "warning" | "error";
type Segment = { text: string; mention: false } | { text: string; mention: true; status: PillStatus | null };

// A tag would cover PiiHighlightOverlay's mark underneath it, so the pill takes the mark's colour instead.
function pillStatus(mention: ComposerMention): PillStatus | null {
    const overlaps = (span: Span) => span.start < mention.end && span.end > mention.start;
    if (errorSpans.some(overlaps)) {
        return "error";
    }
    return warnSpans.some(overlaps) ? "warning" : null;
}

const segments = computed(() => {
    const result: Segment[] = [];
    let cursor = 0;
    for (const mention of [...props.mentions].sort((a, b) => a.start - b.start)) {
        if (mention.start < cursor) {
            continue;
        }
        result.push(
            { text: props.text.slice(cursor, mention.start), mention: false },
            { text: props.text.slice(mention.start, mention.end), mention: true, status: pillStatus(mention) },
        );
        cursor = mention.end;
    }
    // The zero-width space keeps a trailing newline's empty line, so the mirror scrolls as far as the textarea.
    result.push({ text: `${props.text.slice(cursor)}\u200b`, mention: false });
    return result;
});

function measure() {
    const field = props.textarea;
    if (!field || !props.mentions.length) {
        return;
    }
    const computedStyle = getComputedStyle(field);
    const style: Record<string, string> = {};
    for (const property of COPIED_PROPERTIES) {
        style[property] = computedStyle[property];
    }
    const font = measureFont(computedStyle);
    const fontSize = parseFloat(computedStyle.fontSize);
    const lineHeight = parseFloat(computedStyle.lineHeight) || fontSize * 1.2;
    const padY = Math.min(MAX_PILL_PAD_Y, Math.max(0, (lineHeight - font.contentHeight - MIN_LINE_GAP) / 2));
    // clientWidth leaves out a scrollbar, so the mirror wraps at the same width as the visible text.
    mirrorStyle.value = {
        ...style,
        "--composer-mention-at-width": `${font.atWidth}px`,
        "--composer-mention-pad-y": `${padY}px`,
        top: `${field.offsetTop + field.clientTop}px`,
        left: `${field.offsetLeft + field.clientLeft}px`,
        width: `${field.clientWidth}px`,
        height: `${field.clientHeight}px`,
    };
    void nextTick(syncScroll);
}

// Pills on consecutive lines must not touch, so their extra height comes out of the gap between lines,
// which is narrower at the 16px mobile size than at 14px.
const MAX_PILL_PAD_Y = 2;
const MIN_LINE_GAP = 3;

let measuringContext: CanvasRenderingContext2D | null | undefined;
function measureFont(computedStyle: CSSStyleDeclaration) {
    const fontSize = parseFloat(computedStyle.fontSize);
    measuringContext ??= document.createElement("canvas").getContext("2d");
    if (!measuringContext) {
        return { atWidth: 0, contentHeight: fontSize * 1.2 };
    }
    measuringContext.font = `${computedStyle.fontStyle} ${computedStyle.fontWeight} ${computedStyle.fontSize} ${computedStyle.fontFamily}`;
    const metrics = measuringContext.measureText("@");
    // The font bounding box is the content area an inline background covers.
    const contentHeight = metrics.fontBoundingBoxAscent + metrics.fontBoundingBoxDescent || fontSize * 1.2;
    return { atWidth: metrics.width, contentHeight };
}

function syncScroll() {
    if (mirror.value && props.textarea) {
        mirror.value.scrollTop = props.textarea.scrollTop;
    }
}

useResizeObserver(() => props.textarea, measure);
useEventListener(() => props.textarea, "scroll", syncScroll, { passive: true });
// The font is measured with canvas, which reads the fallback font until the web font has loaded.
void document.fonts?.ready.then(measure);
watch(
    [() => props.text, () => props.mentions.length > 0, () => props.textarea],
    () => {
        void nextTick(measure);
    },
    { immediate: true },
);
</script>

<style scoped>
@reference "../../../css/tailwind.css";
@layer components {
    .composer-mention-highlights {
        @apply pointer-events-none absolute box-border overflow-hidden whitespace-pre-wrap wrap-break-word text-ink;
    }

    /* Matches the linked person pill in messages (ChatPersonMention), which shows the name without its @.
       Every glyph has to stay where the textarea put it, so nothing here may change the layout: the @
       keeps its width but is not painted, the name is nudged back by half of it (relative positioning
       moves only the paint), which leaves equal room either side as the pill's padding, and the
       height comes from vertical padding, which an inline element paints without changing the line box.
       Nothing extends sideways, so neighbouring tags keep the full width of the space between them. */
    .composer-mention-pill {
        @apply person-mention-linked-paint box-decoration-clone;
        padding-block: var(--composer-mention-pad-y, 0px);
    }
    .composer-mention-pill-warning {
        @apply bg-status-warning-soft text-ink;
    }
    .composer-mention-pill-error {
        @apply bg-status-error-soft text-ink;
    }
    .composer-mention-at {
        @apply text-transparent;
    }
    .composer-mention-name {
        @apply relative;
        left: calc(var(--composer-mention-at-width, 0px) / -2);
    }
}
</style>
